/**
 * Frame sources for in-browser export: still images and forward-decoded video
 * (WebCodecs via mediabunny). Each source answers `frame(j)` for output frame `j` of its clip.
 */
import { ALL_FORMATS, BlobSource, Input, VideoSampleSink } from 'mediabunny';
import { fetchExportMediaBlob, normalizeExportMediaRef } from '@/lib/exportMediaStaging';
import { clipTrimWindow } from '@/lib/localExport/timelinePlan';

/** `{ image, width, height }` drawable, or null for black. */
function drawable(image, width, height) {
  if (!image || !(width > 0) || !(height > 0)) return null;
  return { image, width, height };
}

export class MediaCache {
  constructor() {
    this.blobs = new Map();
    this.bitmaps = new Map();
  }

  blob(url) {
    const ref = normalizeExportMediaRef(url);
    if (!this.blobs.has(ref)) {
      const p = fetchExportMediaBlob(ref);
      p.catch(() => this.blobs.delete(ref));
      this.blobs.set(ref, p);
    }
    return this.blobs.get(ref);
  }

  /** Blobs are only held until decoded (audio buffer, bitmap, or open demuxer). */
  releaseBlob(url) {
    this.blobs.delete(normalizeExportMediaRef(url));
  }

  /**
   * Decoded still, shared by every clip / overlay that uses the same URL. Each acquire
   * must be paired with {@link releaseBitmap}; the bitmap is freed when the last user lets go.
   */
  acquireBitmap(url) {
    const ref = normalizeExportMediaRef(url);
    let entry = this.bitmaps.get(ref);
    if (!entry) {
      const promise = this.blob(ref).then((b) => {
        this.releaseBlob(ref);
        return createImageBitmap(b);
      });
      entry = { promise, refs: 0 };
      promise.catch(() => {
        if (this.bitmaps.get(ref) === entry) this.bitmaps.delete(ref);
      });
      this.bitmaps.set(ref, entry);
    }
    entry.refs += 1;
    return entry.promise;
  }

  releaseBitmap(url) {
    const ref = normalizeExportMediaRef(url);
    const entry = this.bitmaps.get(ref);
    if (!entry) return;
    entry.refs -= 1;
    if (entry.refs <= 0) {
      this.bitmaps.delete(ref);
      entry.promise.then((bmp) => bmp.close?.()).catch(() => {});
    }
  }

  close() {
    for (const entry of this.bitmaps.values()) {
      entry.promise.then((bmp) => bmp.close?.()).catch(() => {});
    }
    this.bitmaps.clear();
    this.blobs.clear();
  }
}

export class StillClipSource {
  constructor(bitmap, release) {
    this.bitmap = bitmap;
    this.release = release;
  }

  async frame() {
    return drawable(this.bitmap, this.bitmap?.width, this.bitmap?.height);
  }

  close() {
    this.release?.();
    this.release = null;
    this.bitmap = null;
  }
}

/**
 * Forward-only reader over a trimmed video window, sampled every `speed / fps` source
 * seconds like the server. Frames past the playable window fall back to the clip's
 * still image (or hold the last video frame). Seeking backwards restarts the decoder.
 */
export class VideoClipSource {
  static async open(item, cache, fps) {
    const blob = await cache.blob(item.video_url);
    cache.releaseBlob(item.video_url);
    const input = new Input({ source: new BlobSource(blob), formats: ALL_FORMATS });
    try {
      const track = await input.getPrimaryVideoTrack();
      if (!track) throw new Error('No video track');
      if (!(await track.canDecode())) throw new Error('This browser cannot decode the clip codec');
      const [first, duration] = await Promise.all([
        track.getFirstTimestamp(),
        track.computeDuration(),
      ]);
      let fill = null;
      let releaseFill = null;
      if (item.image_url) {
        fill = await cache.acquireBitmap(item.image_url).catch(() => null);
        if (fill) releaseFill = () => cache.releaseBitmap(item.image_url);
      }
      const src = new VideoClipSource(input, track, item, fps, first, duration, fill);
      src.releaseFill = releaseFill;
      return src;
    } catch (err) {
      input.dispose();
      throw err;
    }
  }

  constructor(input, track, item, fps, first, duration, fill) {
    this.input = input;
    this.sink = new VideoSampleSink(track);
    this.first = Number(first) || 0;
    this.fill = fill;
    const { trimStart, trimEnd, speed } = clipTrimWindow(item);
    const end = Math.min(trimEnd, Math.max(0, (Number(duration) || 0) - this.first));
    this.trimStart = trimStart;
    this.dtSource = speed / fps;
    this.videoFrames = end > trimStart + 1e-6
      ? Math.max(1, Math.ceil((end - 1e-4 - trimStart) / this.dtSource))
      : 0;
    this.iter = null;
    this.current = null;
    this.pending = null;
    this.lastJ = -1;
  }

  async restartAt(ts) {
    await this.iter?.return?.();
    this.current?.close();
    this.pending?.close();
    this.current = null;
    this.pending = null;
    this.iter = this.sink.samples(ts)[Symbol.asyncIterator]();
    this.ended = false;
  }

  async advanceTo(ts) {
    const eps = 1e-4;
    if (!this.current) {
      const r = await this.iter.next();
      if (r.done) {
        this.ended = true;
        return;
      }
      this.current = r.value;
    }
    while (!this.ended) {
      if (!this.pending) {
        const r = await this.iter.next();
        if (r.done) {
          this.ended = true;
          break;
        }
        this.pending = r.value;
      }
      if (this.pending.timestamp <= ts + eps) {
        this.current.close();
        this.current = this.pending;
        this.pending = null;
      } else {
        break;
      }
    }
  }

  async frame(j) {
    if (j >= this.videoFrames) {
      if (this.fill) return drawable(this.fill, this.fill.width, this.fill.height);
      if (!this.videoFrames) return null;
      j = this.videoFrames - 1;
    }
    const ts = this.first + this.trimStart + j * this.dtSource;
    if (!this.iter || j < this.lastJ) await this.restartAt(ts);
    this.lastJ = j;
    await this.advanceTo(ts);
    const s = this.current;
    if (!s) return null;
    return drawable(s.toCanvasImageSource(), s.displayWidth, s.displayHeight);
  }

  close() {
    this.iter?.return?.().catch(() => {});
    this.current?.close();
    this.pending?.close();
    this.current = null;
    this.pending = null;
    this.input.dispose();
    this.releaseFill?.();
    this.releaseFill = null;
    this.fill = null;
  }
}

/** Open the right source for a timeline clip; video falls back to its still like the server. */
export async function openClipSource(item, cache, fps) {
  if (item.video_url) {
    try {
      return await VideoClipSource.open(item, cache, fps);
    } catch (err) {
      if (!item.image_url) throw err;
    }
  }
  if (item.image_url) {
    const bitmap = await cache.acquireBitmap(item.image_url);
    return new StillClipSource(bitmap, () => cache.releaseBitmap(item.image_url));
  }
  return null;
}
